import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Users, Clock, MapPin, Share2, Heart, Twitter, Facebook, Link as LinkIcon, ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import CampaignCard from "@/components/CampaignCard";
import ProgressBar from "@/components/ProgressBar";
import PledgeModal from "@/components/PledgeModal";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fetchCampaign, fetchCampaigns, resolveMediaUrl } from "@/lib/api";

const CampaignDetails = () => {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [related, setRelated] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pledgeOpen, setPledgeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { requireAuth } = useAuth();

  const reload = async () => {
    try {
      const c = await fetchCampaign(id);
      setCampaign(c);
      setSelected(c.rewards?.[0]?.id || null);
      const all = await fetchCampaigns({ category: c.category.toLowerCase().replace(" ", "-") });
      setRelated(all.filter((r) => r.id !== c.id).slice(0, 3));
    } catch (e) {
      setCampaign(false); // not found
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setLoading(true); reload(); /* eslint-disable-next-line */ }, [id]);

  const handleBack = () => {
    if (!requireAuth("Sign in to back this project.")) return;
    setPledgeOpen(true);
  };
  const handleSave = () => {
    if (!requireAuth("Sign in to save this project.")) return;
    toast.success("Saved to your list");
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-navy-500"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!campaign) return <div className="min-h-[50vh] grid place-items-center text-navy-500">Campaign not found</div>;

  const percent = Math.round((campaign.raised / campaign.goal) * 100);
  const creator = campaign.creator || {};

  return (
    <div data-testid="campaign-details-page">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-8">
        <Link to="/explore" className="inline-flex items-center gap-1.5 text-sm text-navy-500 hover:text-saffron-600 transition-colors" data-testid="back-to-explore">
          <ChevronLeft className="h-4 w-4" /> Back to explore
        </Link>
      </div>

      {/* Hero: video takes precedence over cover image */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-6" data-testid="campaign-hero-media">
        {campaign.video_url ? (
          (() => {
            const url = campaign.video_url;
            const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
            const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
            const embed = yt ? `https://www.youtube.com/embed/${yt[1]}` : vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
            return (
              <div className="rounded-3xl overflow-hidden bg-black aspect-video">
                {embed ? (
                  <iframe title={campaign.title} src={embed} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <video controls poster={campaign.cover} src={resolveMediaUrl(url)} className="w-full h-full" />
                )}
              </div>
            );
          })()
        ) : (
          <div className="rounded-3xl overflow-hidden bg-slate-100 aspect-[21/9]">
            <img src={campaign.cover} alt={campaign.title} className="h-full w-full object-cover" />
          </div>
        )}
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <span className="text-xs font-medium text-saffron-700 bg-saffron-50 rounded-full px-2.5 py-1">{campaign.category}</span>
            <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">{campaign.title}</h1>
            <p className="mt-3 text-lg text-navy-600 leading-relaxed">{campaign.tagline}</p>

            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm">
              <Link to={`/creator/${creator.user_id}`} className="flex items-center gap-2 group" data-testid="campaign-creator-link">
                {creator.avatar_url && <img src={resolveMediaUrl(creator.avatar_url)} alt={creator.name} className="h-10 w-10 rounded-full object-cover" />}
                <div>
                  <div className="font-medium text-navy-800 group-hover:text-saffron-600 transition-colors">{creator.name}</div>
                  <div className="text-xs text-navy-500">Creator</div>
                </div>
              </Link>
              {campaign.location && <span className="hidden sm:inline-flex items-center gap-1.5 text-navy-500"><MapPin className="h-4 w-4" /> {campaign.location}</span>}
            </div>

            <Tabs defaultValue="story" className="mt-10" data-testid="campaign-tabs">
              <TabsList className="rounded-2xl bg-slate-100 p-1 h-auto">
                <TabsTrigger value="story" className="rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Story</TabsTrigger>
                <TabsTrigger value="gallery" className="rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Gallery</TabsTrigger>
                <TabsTrigger value="updates" className="rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Updates ({(campaign.updates || []).length})</TabsTrigger>
                <TabsTrigger value="comments" className="rounded-xl px-4 py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">Comments</TabsTrigger>
              </TabsList>

              <TabsContent value="story" className="mt-8">
                <div className="prose max-w-none">
                  <p className="text-navy-700 leading-relaxed text-lg">{campaign.story}</p>
                  <h3 className="mt-10 font-display text-2xl font-semibold tracking-tight text-navy-800">What your pledge enables</h3>
                  <ul className="mt-4 space-y-3 text-navy-700">
                    <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-600 flex-shrink-0" /> Final tooling and small-batch production runs</li>
                    <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-600 flex-shrink-0" /> Independent third-party quality certification</li>
                    <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-600 flex-shrink-0" /> Fair-wage manufacturing in partnership with family-owned workshops</li>
                    <li className="flex items-start gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-600 flex-shrink-0" /> Global fulfillment to 120+ countries with tracked shipping</li>
                  </ul>
                </div>
              </TabsContent>

              <TabsContent value="gallery" className="mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(campaign.gallery || []).map((src, i) => (
                    <div key={i} className="rounded-2xl overflow-hidden aspect-[4/3] bg-slate-100 hover:scale-[1.01] transition-transform">
                      <img src={src} alt="" className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="updates" className="mt-8 space-y-6">
                {(!campaign.updates || campaign.updates.length === 0) ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-navy-500">No updates yet.</div>
                ) : campaign.updates.map((u) => (
                  <div key={u.id} className="rounded-2xl border border-warm-200 p-6 bg-white">
                    <div className="text-xs text-navy-500">{u.date}</div>
                    <h4 className="mt-1 font-display text-lg font-semibold text-navy-800">{u.title}</h4>
                    <p className="mt-2 text-navy-600 leading-relaxed">{u.body}</p>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="comments" className="mt-8">
                <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-navy-500">Comments will appear here once backers start pledging.</div>
              </TabsContent>
            </Tabs>
          </div>

          <aside className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <div className="rounded-2xl border border-warm-200 bg-white p-6" data-testid="funding-box">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="font-display text-4xl font-semibold text-navy-800">${campaign.raised.toLocaleString()}</div>
                    <div className="text-sm text-navy-500 mt-1">raised of ${campaign.goal.toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-3xl font-semibold text-saffron-600">{percent}%</div>
                    <div className="text-xs text-navy-500">funded</div>
                  </div>
                </div>
                <div className="mt-4"><ProgressBar value={Math.min(percent, 100)} /></div>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  <div>
                    <div className="font-display text-2xl font-semibold text-navy-800 inline-flex items-center gap-1"><Users className="h-4 w-4 text-slate-400" />{campaign.backers.toLocaleString()}</div>
                    <div className="text-xs text-navy-500">backers</div>
                  </div>
                  <div>
                    <div className="font-display text-2xl font-semibold text-navy-800 inline-flex items-center gap-1"><Clock className="h-4 w-4 text-slate-400" />{campaign.days_left}</div>
                    <div className="text-xs text-navy-500">days left</div>
                  </div>
                </div>
                <Button
                  className="mt-6 w-full rounded-2xl bg-saffron-600 hover:bg-saffron-700 h-12 shadow-sm hover:scale-[1.02] active:scale-95 transition-all"
                  onClick={handleBack}
                  data-testid="support-project-btn"
                >
                  Back this project
                </Button>
                <div className="mt-3 flex items-center gap-2">
                  <Button variant="outline" className="flex-1 rounded-2xl" onClick={handleSave}>
                    <Heart className="h-4 w-4 mr-2" /> Save
                  </Button>
                  <Button variant="outline" className="flex-1 rounded-2xl" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link copied to clipboard"); }}>
                    <Share2 className="h-4 w-4 mr-2" /> Share
                  </Button>
                </div>
                <div className="mt-4 flex items-center justify-center gap-3 text-slate-400">
                  <button className="hover:text-saffron-600 transition-colors" aria-label="Share on Twitter"><Twitter className="h-4 w-4" /></button>
                  <button className="hover:text-saffron-600 transition-colors" aria-label="Share on Facebook"><Facebook className="h-4 w-4" /></button>
                  <button className="hover:text-saffron-600 transition-colors" aria-label="Copy link"><LinkIcon className="h-4 w-4" /></button>
                </div>
              </div>

              <div className="rounded-2xl border border-warm-200 bg-white p-6" data-testid="rewards-list">
                <h3 className="font-display text-lg font-semibold text-navy-800">Reward tiers</h3>
                <div className="mt-4 space-y-3">
                  {(campaign.rewards || []).map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setSelected(r.id)}
                      data-testid={`reward-${r.id}`}
                      className={`w-full text-left rounded-2xl border p-5 transition-all ${selected === r.id ? "border-saffron-500 bg-saffron-50/40 ring-2 ring-saffron-500/20" : "border-warm-200 hover:border-saffron-200"}`}
                    >
                      <div className="flex justify-between items-baseline">
                        <div className="font-display text-xl font-semibold text-navy-800">${r.price}</div>
                        <div className="text-xs text-navy-500">{r.claimed}/{r.limit} claimed</div>
                      </div>
                      <div className="mt-1 font-medium text-navy-800">{r.title}</div>
                      <p className="mt-1 text-sm text-navy-600">{r.description}</p>
                      <div className="mt-2 text-xs text-navy-500">Estimated delivery: {r.estimated}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="border-t border-warm-200 bg-cream-100/70">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="font-display text-3xl font-semibold tracking-tight text-navy-800">Related campaigns</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <PledgeModal open={pledgeOpen} onOpenChange={setPledgeOpen} campaign={campaign} onSuccess={reload} />
    </div>
  );
};

export default CampaignDetails;
